import { useEffect, useState } from "react";
import axios from "axios";
import { Layers, X } from "lucide-react";
import { API } from "../../lib/api";
import { TIM_COLOR } from "../../lib/dashConstants";
import { fmtDate, fmtN } from "../../lib/format";

export default function DrillDownModal({ open, onClose, drill, filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subFilters, setSubFilters] = useState({});
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !drill) return;
    setLoading(true);
    setSubFilters({}); setSearch("");
    const params = { ...filters };
    if (drill.key) params.drill_key = drill.key;
    if (drill.value !== undefined && drill.value !== null) params.drill_value = String(drill.value);
    params.limit = 200;
    axios.get(`${API}/sales/drilldown`, { params })
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, drill, filters]);

  if (!open) return null;

  const allRows = data?.rows || [];
  const FIELDS = [
    ["nama_sales", "Sales"], ["tim_sales", "Tim"], ["status_sekolah", "Status"],
    ["tujuan_sales", "Tujuan"], ["kegiatan", "Kegiatan"], ["dilakukan_via", "Via"],
  ];
  const optionsFor = (k) => Array.from(new Set(
    allRows.map((r) => String(r[k] ?? "")).filter((v) => v && v !== "undefined" && v !== "None")
  )).sort();
  const rows = allRows.filter((r) => {
    for (const [k] of FIELDS) {
      if (subFilters[k] && String(r[k] ?? "") !== subFilters[k]) return false;
    }
    if (search) {
      const hay = Object.values(r).map((v) => String(v ?? "")).join(" ").toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="drilldown-modal">
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: "var(--navy)" }}>
              <Layers size={18} style={{ verticalAlign: "-3px", marginRight: 8 }} /> Drill-Down
            </h2>
            <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "var(--muted)" }}>
              {drill?.title || "Detail data yang menyusun metrik ini"}
              {data && <> · <b>{fmtN(rows.length)}</b> dari {fmtN(allRows.length)} ditampilkan</>}
            </p>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        {!loading && allRows.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", margin: "10px 0" }}
               data-testid="drilldown-filters">
            <input className="select-input" placeholder="Cari di hasil…" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="drilldown-search" style={{ minWidth: 160 }} />
            {FIELDS.map(([k, label]) => {
              const opts = optionsFor(k);
              if (opts.length === 0) return null;
              return (
                <select key={k} className="select-input" value={subFilters[k] || ""}
                  onChange={(e) => setSubFilters({ ...subFilters, [k]: e.target.value })}
                  data-testid={`drilldown-filter-${k}`} style={{ minWidth: 120 }}>
                  <option value="">{label}: Semua</option>
                  {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              );
            })}
            <button className="btn btn-ghost" style={{ padding: "6px 10px", fontSize: 12 }}
              onClick={() => { setSubFilters({}); setSearch(""); }} data-testid="drilldown-filter-clear">
              Reset Filter
            </button>
          </div>
        )}
        <div style={{ maxHeight: "60vh", overflow: "auto", border: "1px solid var(--border-2)", borderRadius: 8 }}>
          {loading ? <div className="empty">Memuat detail...</div> :
            rows.length === 0 ? <div className="empty">Tidak ada record yang cocok dengan filter.</div> :
            <table className="tbl" data-testid="drilldown-table">
              <thead>
                <tr>
                  <th style={{width:130}}>Tanggal</th>
                  <th>Sales</th>
                  <th>Tim</th>
                  <th>Status</th>
                  <th>NPSN</th>
                  <th>Nama Sekolah</th>
                  <th>Tujuan</th>
                  <th>Kegiatan</th>
                  <th>Via</th>
                  <th>Detail Issue</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r,i) => (
                  <tr key={i}>
                    <td style={{whiteSpace:"nowrap",fontSize:11,color:"var(--muted)"}}>{fmtDate(r.tanggal_visit)}</td>
                    <td style={{fontWeight:600,color:"var(--navy)"}}>{r.nama_sales || "—"}</td>
                    <td>{r.tim_sales ? <span className="badge" style={{ background: TIM_COLOR[r.tim_sales]+"22", color: TIM_COLOR[r.tim_sales], borderColor: TIM_COLOR[r.tim_sales]+"55" }}>{r.tim_sales}</span> : "—"}</td>
                    <td><span className="badge">{r.status_sekolah || "—"}</span></td>
                    <td style={{fontVariantNumeric:"tabular-nums",fontSize:12,color:"var(--muted)"}}>{r.npsn || "—"}</td>
                    <td>{r.nama_sekolah || <span style={{color:"var(--muted)",fontStyle:"italic",fontSize:11}}>(kosong)</span>}</td>
                    <td><span className={`badge ${r.tujuan_sales?.includes("EFT") ? "warn" : ""}`}>{r.tujuan_sales || "—"}</span></td>
                    <td>{r.kegiatan || "—"}</td>
                    <td><span className="badge">{r.dilakukan_via || "—"}</span></td>
                    <td style={{fontSize:11,color:"var(--text)"}}>{r.detail_issue || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </div>
      </div>
    </div>
  );
}